import { Dispatch, SetStateAction } from 'react';
import { Avatar, Grid, Typography, useTheme } from '@mui/material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { OperationActivity } from '../../../../networkRequest/operationsService';
import { ActivityChip, ActivityMenu, OperationOfficialAvatar } from '@pages/Operations/components';
import Utils from '../../../../utils/utils';
import { PagePaths } from '../../../../utils/PagePaths';
import { formatDateV2 } from '../../../../utils/formatDate';

interface ActivityProps {
  operationId: string;
  activity: OperationActivity;
  onEdit: () => void;
  onDelete: () => void;
  setSelectedItem: Dispatch<SetStateAction<OperationActivity | null>>;
  handleCreateActivityIncident: (activity: OperationActivity) => Promise<void>;
}

const Activity = ({
  operationId,
  activity,
  onEdit,
  onDelete,
  setSelectedItem,
  handleCreateActivityIncident,
}: ActivityProps) => {
  const theme = useTheme();

  const authorName = Utils.fullName(
    activity.author.agency_official.first_name,
    activity.author.agency_official.last_name
  );

  const incidentPagePath = (activity.incident_id !== null) ?
    PagePaths.operationIncident(operationId, activity.incident_id) : undefined;

  return (
    <Grid
      id={`activity_${activity.id}`}
      sx={{
        backgroundColor: theme.palette.grey[50],
        padding: '16px',
        marginBottom: '16px',
        borderRadius: '10px'
      }}
    >
      <Grid container sx={{
        justifyContent: 'space-between'
      }}>
        <Grid container>
          <Avatar
            sx={{ width: '24px', height: '24px', marginRight: '8px' }}
            alt={authorName}
            src={activity.author.agency_official.img_url || undefined}
          />
          <Grid container sx={{ flexDirection: 'column' }}>
            <Typography>{authorName}</Typography>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {formatDateV2(activity.performed_at)}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            alignItems: 'center',
            gap: 1
          }}>
          <ActivityChip
            label="Incident"
            icon={<ReportGmailerrorredIcon />}
            active={activity.incident_id !== null}
            linkPath={incidentPagePath}
            onClick={() => handleCreateActivityIncident(activity)}
          />
          <ActivityMenu
            item={activity}
            onEdit={onEdit}
            onDelete={onDelete}
            setSelectedItem={setSelectedItem}
          />
        </Grid>
      </Grid>

      <Typography
        sx={{
          mt: '16px',
          whiteSpace: 'pre-line'
        }}>
        {activity.description}
      </Typography>

      {
        activity.officials?.length
          ? (
            <Grid
              container
              sx={{
                gap: 1,
                mt: '16px'
              }}>
              {activity.officials.map(({ agency_official }) => (
                <OperationOfficialAvatar key={agency_official.id} agency_official={agency_official} />
              ))}
            </Grid>
          )
          : null
      }
    </Grid>
  );
};

export default Activity;